import { ref } from "vue";
import { $http } from "@/plugins/api";
import type { Collection, Params } from "@/types/api";
import { createSharedComposable } from "@vueuse/core";
import { mapExemptionDTO } from "./Exemptions.utils.ts";
import { getApiClientUrl } from "@/config/runtimeConfig";
import {
  Exemption,
  ExemptionCondition,
  ExemptionDTO,
  ExemptionMutationResponse,
  ExemptionsFilter,
  ExemptionsType,
} from "./exemptions.types.ts";

export const useExemptions = createSharedComposable(() => {
  const initialCountries: Collection<Exemption> = {
    current_page: 1,
    page_size: 100,
    results: [],
    total: 0,
  };

  const exemptions = ref<Collection<Exemption>>(initialCountries);

  const freeProviderDomains = ref<string[]>([]);
  const isFetchingFreeProviderDomains = ref(false);

  const getFreeProviderDomains = async (): Promise<void> => {
    const apiClientUrl = getApiClientUrl();
    try {
      isFetchingFreeProviderDomains.value = true;
      const res = await $http.get<Collection<{ value: string }>>(
        `${apiClientUrl}/v1/allow-block/0c9706a0-86a1-46bf-b8ac-7ea52c2db44b/actions/domain/free`,
      );
      freeProviderDomains.value = res.data.results.map((d) => d.value);
    } finally {
      isFetchingFreeProviderDomains.value = false;
    }
  };

  const getExemptions = async ({
    globalOrganisationID,
    type,
    params,
  }: {
    globalOrganisationID: string | null;
    type: ExemptionsFilter;
    params: Params;
  }): Promise<void> => {
    if (globalOrganisationID === null) {
      throw new Error("Organisation ID is not provided");
    }

    const paramsWithoutEmptyValues = Object.fromEntries(
      Object.entries(params).filter((param) => param[1]),
    );
    const searchParams = new URLSearchParams(paramsWithoutEmptyValues);
    const apiClientUrl = getApiClientUrl();
    const res = await $http.get<Collection<ExemptionDTO>>(
      `${apiClientUrl}/v1/allow-block/${globalOrganisationID}/exemptions/${type}?${searchParams.toString()}`,
      { _hasCustomErrorHandling: true },
    );
    if (res.status === 200) {
      exemptions.value = {
        ...res.data,
        results: res.data.results.map(mapExemptionDTO),
      };
    }
  };

  const addExemption = async ({
    organisationID,
    type,
    payload,
  }: {
    organisationID: string;
    type: ExemptionsType;
    payload: {
      value: string;
      skipped_checks: string[];
      comment: string;
      secondary_condition?: ExemptionCondition;
    };
  }): Promise<ExemptionMutationResponse> => {
    const apiClientUrl = getApiClientUrl();
    const res = await $http.post<ExemptionMutationResponse>(
      `${apiClientUrl}/v1/allow-block/${organisationID}/exemptions/${type}`,
      { ...payload },
      { _hasCustomErrorHandling: true },
    );
    return res.data;
  };

  const updateExemption = async ({
    organisationID,
    type,
    id,
    payload,
  }: {
    organisationID: string;
    type: ExemptionsType;
    id: string;
    payload: {
      value: string;
      skipped_checks: string[];
      comment: string;
      secondary_condition?: ExemptionCondition;
    };
  }): Promise<ExemptionMutationResponse> => {
    const apiClientUrl = getApiClientUrl();
    const res = await $http.put<ExemptionMutationResponse>(
      `${apiClientUrl}/v1/allow-block/${organisationID}/exemptions/${id}/${type}`,
      { ...payload },
      { _hasCustomErrorHandling: true },
    );
    return res.data;
  };

  const deleteExemption = async ({
    organisationID,
    ids,
    type,
  }: {
    organisationID: string;
    ids: string[];
    type: ExemptionsFilter;
  }): Promise<void> => {
    const apiClientUrl = getApiClientUrl();
    await $http.delete(
      `${apiClientUrl}/v1/allow-block/${organisationID}/exemptions/${type}`,
      {
        data: { ids },
        _hasCustomErrorHandling: true,
      },
    );
  };

  return {
    exemptions,
    getExemptions,
    addExemption,
    updateExemption,
    deleteExemption,
    freeProviderDomains,
    isFetchingFreeProviderDomains,
    getFreeProviderDomains,
  };
});
